import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  selectAllOrders, 
  selectOrdersSummary, 
  removeOrder,
  updateOrder 
} from '../store/ordersSlice';

const OrderHistory = () => {
  const dispatch = useDispatch();
  const orders = useSelector(selectAllOrders);
  const summary = useSelector(selectOrdersSummary);
  
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');

  const filteredAndSortedOrders = useMemo(() => {
    let filtered = [...orders];

    // Apply type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(order => order.type === filterType);
    }

    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(order => order.status === filterStatus);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'timestamp':
          aValue = new Date(a.timestamp);
          bValue = new Date(b.timestamp);
          break;
        case 'quantity':
          aValue = a.quantity;
          bValue = b.quantity;
          break;
        case 'price':
          aValue = a.price;
          bValue = b.price;
          break;
        case 'value':
          aValue = a.quantity * a.price;
          bValue = b.quantity * b.price;
          break;
        case 'entityName':
          aValue = a.entityName.toLowerCase();
          bValue = b.entityName.toLowerCase();
          break;
        default:
          aValue = a[sortBy];
          bValue = b[sortBy];
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [orders, filterType, filterStatus, sortBy, sortOrder]);

  const handleRemoveOrder = (orderId) => {
    if (window.confirm('Are you sure you want to remove this order?')) {
      dispatch(removeOrder(orderId));
    }
  };

  const handleStatusChange = (orderId, newStatus) => {
    dispatch(updateOrder({ id: orderId, updates: { status: newStatus } }));
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#ff9800';
      case 'completed': return '#4caf50';
      case 'cancelled': return '#f44336';
      case 'processing': return '#2196f3';
      case 'fulfilled': return '#1976d2';
      default: return '#666';
    }
  };

  const getTypeColor = (type) => {
    return type === 'buy' ? '#4caf50' : '#f44336';
  };

  return (
    <div style={{
      border: '1px solid #ddd',
      borderRadius: 8,
      padding: 20,
      background: '#fafcff',
      fontFamily: 'Arial, sans-serif'
    }}>
      <h3 style={{ margin: '0 0 16px 0', color: '#333' }}>
        Order History ({summary.totalOrders} orders)
      </h3>

      {/* Summary Statistics */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 16,
        marginBottom: 20
      }}>
        <div style={{
          background: '#e8f5e8',
          padding: 12,
          borderRadius: 6,
          border: '1px solid #4caf50'
        }}>
          <div style={{ fontWeight: 'bold', color: '#2e7d32' }}>Total Orders</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2e7d32' }}>
            {summary.totalOrders}
          </div>
        </div>
        
        <div style={{
          background: '#e8f5e8',
          padding: 12,
          borderRadius: 6,
          border: '1px solid #4caf50'
        }}>
          <div style={{ fontWeight: 'bold', color: '#2e7d32' }}>Buy Orders</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2e7d32' }}>
            {summary.buyOrders}
          </div>
        </div>
        
        <div style={{
          background: '#ffebee',
          padding: 12,
          borderRadius: 6,
          border: '1px solid #f44336'
        }}>
          <div style={{ fontWeight: 'bold', color: '#c62828' }}>Sell Orders</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#c62828' }}>
            {summary.sellOrders}
          </div>
        </div>
        
        <div style={{
          background: '#e3f2fd',
          padding: 12,
          borderRadius: 6,
          border: '1px solid #2196f3'
        }}>
          <div style={{ fontWeight: 'bold', color: '#1976d2' }}>Total Value</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1976d2' }}>
            {formatCurrency(summary.totalValue)}
          </div>
        </div>
      </div>

      {/* Filters and Sorting */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: 12,
        marginBottom: 16
      }}>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', color: '#555', fontSize: 12 }}>
            Filter by Type:
          </label>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            style={{
              width: '100%',
              padding: 6,
              border: '1px solid #ccc',
              borderRadius: 4,
              fontSize: 14
            }}
          >
            <option value="all">All Types</option>
            <option value="buy">Buy Orders</option>
            <option value="sell">Sell Orders</option>
          </select>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', color: '#555', fontSize: 12 }}>
            Filter by Status:
          </label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{
              width: '100%',
              padding: 6,
              border: '1px solid #ccc',
              borderRadius: 4,
              fontSize: 14
            }}
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', color: '#555', fontSize: 12 }}>
            Sort by:
          </label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              width: '100%',
              padding: 6,
              border: '1px solid #ccc',
              borderRadius: 4,
              fontSize: 14
            }}
          >
            <option value="timestamp">Date/Time</option>
            <option value="entityName">Entity</option>
            <option value="quantity">Quantity</option>
            <option value="price">Price</option>
            <option value="value">Value</option>
          </select>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', color: '#555', fontSize: 12 }}>
            Order:
          </label>
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            style={{
              width: '100%',
              padding: 6,
              border: '1px solid #ccc',
              borderRadius: 4,
              fontSize: 14
            }}
          >
            <option value="desc">Newest First</option>
            <option value="asc">Oldest First</option>
          </select>
        </div>
      </div>

      {/* Orders Table */}
      {filteredAndSortedOrders.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: 40,
          color: '#666',
          fontStyle: 'italic',
          background: '#f5f5f5',
          borderRadius: 4
        }}>
          {orders.length === 0 ? 'No orders placed yet.' : 'No orders match the current filters.'}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 14
          }}>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #ddd' }}>Date/Time</th>
                <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #ddd' }}>Entity</th>
                <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #ddd' }}>Unit</th>
                <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #ddd' }}>Type</th>
                <th style={{ padding: 12, textAlign: 'right', borderBottom: '2px solid #ddd' }}>Quantity</th>
                <th style={{ padding: 12, textAlign: 'right', borderBottom: '2px solid #ddd' }}>Price</th>
                <th style={{ padding: 12, textAlign: 'right', borderBottom: '2px solid #ddd' }}>Value</th>
                <th style={{ padding: 12, textAlign: 'center', borderBottom: '2px solid #ddd' }}>Status</th>
                <th style={{ padding: 12, textAlign: 'center', borderBottom: '2px solid #ddd' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedOrders.map((order) => (
                <tr key={order.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: 12, fontSize: 12, color: '#666' }}>
                    {formatTimestamp(order.timestamp)}
                  </td>
                  <td style={{ padding: 12, fontWeight: 'bold' }}>
                    {order.entityName}
                  </td>
                  <td style={{ padding: 12 }}>
                    {order.unit || '-'}
                  </td>
                  <td style={{ padding: 12 }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: 4,
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: 12,
                      backgroundColor: getTypeColor(order.type)
                    }}>
                      {order.type.toUpperCase()}
                    </span>
                  </td>
                  <td style={{ padding: 12, textAlign: 'right', fontWeight: 'bold' }}>
                    {order.quantity.toLocaleString()}
                  </td>
                  <td style={{ padding: 12, textAlign: 'right' }}>
                    {formatCurrency(order.price)}
                  </td>
                  <td style={{ padding: 12, textAlign: 'right', fontWeight: 'bold' }}>
                    {formatCurrency(order.quantity * order.price)}
                  </td>
                  <td style={{ padding: 12, textAlign: 'center' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '4px 10px',
                        borderRadius: 4,
                        fontSize: 12,
                        color: '#fff',
                        fontWeight: 'bold',
                        background: getStatusColor(order.status),
                        minWidth: 80,
                        textAlign: 'center',
                        textTransform: 'capitalize'
                      }}
                    >
                      {order.status === 'fulfilled' ? 'Fulfilled' : (order.status ? order.status.charAt(0).toUpperCase() + order.status.slice(1) : 'Pending')}
                    </span>
                  </td>
                  <td style={{ padding: 12, textAlign: 'center' }}>
                    <button
                      onClick={() => handleRemoveOrder(order.id)}
                      disabled={order.status === 'fulfilled'}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: order.status === 'fulfilled' ? '#ccc' : '#f44336',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        cursor: order.status === 'fulfilled' ? 'not-allowed' : 'pointer',
                        fontSize: 12,
                        opacity: order.status === 'fulfilled' ? 0.6 : 1
                      }}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default OrderHistory; 